import { Children, cloneElement, createContext, memo, useContext, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import _ from 'lodash';
import clsx from 'clsx';

const initialLetters = (text = '') => {
  if (typeof text !== 'string') return '';
  return text
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

const stickyRightCommonStyled = 'md:sticky md:right-0 md:border-l-1';
const stickyLeftCommonStyled = 'md:sticky md:left-0 md:border-r-1';

const stickyRightHeadStyled = 'md:border-l-neutral-700 md:shadow-md z-20';
const stickyLeftHeadStyled = 'md:border-r-neutral-700 md:shadow-md z-20';

const stickyRightRowStyled = 'md:border-l-neutral-400 md:shadow-md z-10';
const stickyLeftRowStyled = 'md:border-r-neutral-400 md:shadow-md z-10';

const TableContext = createContext({});
const { Provider } = TableContext;

const WithProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return <Provider value={{ isCollapsed, setIsCollapsed }}>{children}</Provider>;
};

const Collapsed = memo(({ nestedData }) => {
  const { isCollapsed } = useContext(TableContext);

  const metadata = useMemo(() => {
    if (!nestedData) return [];
    return Object.entries(nestedData).map(([key, value]) => ({
      keyObj: key,
      valueObj: value,
    }));
  }, [nestedData]);

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            {metadata.length > 0 ? (
              <Table size="small" aria-label="metadata">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '100%' }}>Clave</TableCell>
                    <TableCell sx={{ width: '100%' }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metadata.map((rowItem, index) => (
                    <TableRow key={`nestedtable_${rowItem.keyObj}_${index}`}>
                      <TableCell component="th" scope="row">
                        {rowItem.keyObj}
                      </TableCell>
                      <TableCell>{rowItem.valueObj}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-auto w-full py-16 flex-col justify-center items-center">
                <Typography variant="subtitle1" gutterBottom>
                  No hay datos disponibles
                </Typography>
              </div>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    height: '5rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[400], // Cambiar a un color con más contraste
  },
}));

/**
 * Componente de tabla compuesta con soporte para filas anidadas, carga y estados vacíos.
 *
 * @component
 * @param {boolean} [selectable=false] - Determina si las filas son seleccionables.
 * @param {Function} [onRowClick=() => {}] - Función llamada al hacer clic en una fila, recibe el índice y el item.
 * @param {React.ReactNode} children - Columnas a renderizar como hijos del componente.
 * @param {Array<Object>} headers - Encabezados de la tabla, con propiedades como `title`, `width`, `align`, etc.
 * @param {Array<Object>} data - Datos a renderizar en las filas.
 * @param {Function} [metadata=() => {}] - Función opcional para obtener metadata asociada a una fila.
 * @param {boolean} isLoading - Si está cargando los datos.
 * @param {'small'|'medium'} [size='small'] - Tamaño de la tabla.
 * @param {string} [headerBgColor] - Color de fondo para el encabezado.
 * @param {string} [headerTxtColor] - Color del texto en el encabezado.
 * @param {Object} props - Propiedades adicionales pasadas al contenedor de la tabla.
 */
export const ComposedTable = ({
  selectable = false,
  onRowClick = () => {},
  children,
  headers = [],
  data,
  metadata = () => {},
  isLoading,
  size = 'small',
  headerBgColor,
  headerTxtColor,
  ...props
}) => {
  const memoizedChildren = useMemo(
    () => Children.map(children, (child) => cloneElement(child)),
    [children]
  );

  const memoizedRows = useMemo(() => {
    return data.map((item, indexRow) => (
      <WithProvider key={indexRow}>
        <StyledTableRow
          onClick={() => onRowClick(indexRow, item)}
          sx={{ cursor: selectable ? 'pointer' : undefined }}
        >
          {Children.map(memoizedChildren, (child) => cloneElement(child, { item }))}
        </StyledTableRow>
        <Collapsed nestedData={_.has(item, 'metadata') ? item.metadata : undefined} />
      </WithProvider>
    ));
  }, [data, memoizedChildren, onRowClick, selectable]);

  return useMemo(() => {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '0.4rem',
          position: 'relative',
          maxHeight: '180rem',
          minHeight: '16rem',
        }}
        {...props}
      >
        <Table aria-label="composed table component" size={size} stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map(
                (
                  { title, width, minWidth, maxWidth, fixedRight, fixedLeft, align = 'center' },
                  index
                ) => (
                  <StyledTableCell
                    key={`${title}_${index}`}
                    align={align}
                    style={{
                      width,
                      minWidth,
                      maxWidth,
                      backgroundColor: headerBgColor,
                      color: headerTxtColor,
                    }}
                    className={clsx(
                      fixedRight &&
                        !fixedLeft &&
                        `${stickyRightCommonStyled} ${stickyRightHeadStyled}`,
                      !fixedRight &&
                        fixedLeft &&
                        `${stickyLeftCommonStyled} ${stickyLeftHeadStyled}`
                    )}
                  >
                    {title}
                  </StyledTableCell>
                )
              )}
            </TableRow>
          </TableHead>

          {!isLoading && data.length > 0 && <TableBody>{memoizedRows}</TableBody>}
        </Table>

        {!data.length > 0 && !isLoading && (
          <div
            className="bg-slate-950 h-full w-full left-0 top-0 bg-opacity-50 flex flex-auto justify-center items-end"
            style={{ position: 'absolute' }}
          >
            <div className="mt-16 mb-24">
              <Typography variant="body2">No hay datos que mostrar</Typography>
            </div>
          </div>
        )}

        {isLoading && (
          <div
            className="h-full w-full left-0 top-0 flex flex-auto justify-center items-center"
            style={{
              position: 'absolute',
              backgroundColor: 'rgba(56, 56, 56, 0.22)',
              backdropFilter: 'blur(1px)',
              zIndex: 100,
            }}
          >
            <CircularProgress sx={{ color: '#FEFEFE' }} size={60} />
          </div>
        )}
      </TableContainer>
    );
  }, [data, memoizedRows, headers, isLoading, size, headerBgColor, headerTxtColor, props]);
};

const Column = memo(({ fixedRight, fixedLeft, content = () => {}, item }) => {
  const renderedContent = useMemo(() => {
    const rendered = content(item);
    if (typeof rendered === 'string' || typeof rendered === 'number') {
      return (
        <Typography
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 3,
            lineClamp: 3,
            wordBreak: 'break-all',
          }}
        >
          {rendered}
        </Typography>
      );
    }
    return rendered;
  }, [content, item]);

  return (
    <StyledTableCell
      align="center"
      sx={{ backgroundColor: (_theme) => _theme.palette.background.default }}
      className={clsx(
        fixedRight && !fixedLeft && `${stickyRightCommonStyled} ${stickyRightRowStyled}`,
        !fixedRight && fixedLeft && `${stickyLeftCommonStyled} ${stickyLeftRowStyled}`
      )}
    >
      {renderedContent}
    </StyledTableCell>
  );
});

const NestedButton = memo(() => {
  const { isCollapsed, setIsCollapsed } = useContext(TableContext);
  return (
    <IconButton aria-label="expand row" size="small" onClick={() => setIsCollapsed(!isCollapsed)}>
      {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  );
});

const Thumbail = memo(({ baseUri, path, alt, name, color, square, ...props }) => {
  const src = useMemo(() => `${baseUri ?? ''}${baseUri ? '/' : ''}${path}`, [baseUri, path]);
  return (
    <Avatar
      alt={alt}
      sx={{ bgcolor: color }}
      variant={square ? 'square' : undefined}
      src={src}
      {...props}
    >
      {name && initialLetters(name)}
    </Avatar>
  );
});

const Preview = memo(({ baseUri, path, alt, width, height, style }) => {
  const src = useMemo(() => `${baseUri ?? ''}${baseUri ? '/' : ''}${path}`, [baseUri, path]);
  if (!path) {
    return <HideImageOutlinedIcon style={{ width, height, ...style, color: '#a6a6a6' }} />;
  }
  return <img src={src} alt={alt} style={{ width, height, ...style }} />;
});

/**
 * Componente de celda de columna personalizada dentro de la ComposedTable.
 *
 * @component
 * @param {boolean} fixedRight - Si la columna está fija a la derecha.
 * @param {boolean} fixedLeft - Si la columna está fija a la izquierda.
 * @param {Function} content - Función que recibe `item` y devuelve contenido JSX, string o número.
 * @param {Object} item - Objeto de datos correspondiente a la fila actual.
 */
ComposedTable.Column = Column;

/**
 * Botón que alterna la expansión o colapso de una fila anidada en la tabla.
 *
 * @component
 * @returns {JSX.Element}
 */
ComposedTable.NestedButton = memo(NestedButton);

/**
 * Componente para mostrar un avatar o miniatura de imagen.
 *
 * @component
 * @param {string} [baseUri] - Ruta base del recurso de imagen.
 * @param {string} [path] - Ruta relativa del recurso de imagen.
 * @param {string} [alt] - Texto alternativo para la imagen.
 * @param {string} [name] - Nombre a mostrar como iniciales si no hay imagen.
 * @param {string} [color] - Color de fondo del avatar.
 * @param {string} [className] - Clases CSS adicionales.
 * @param {Object} [style] - Estilos en línea.
 * @param {boolean} [square=false] - Si el avatar debe tener forma cuadrada.
 * @param {Object} props - Props adicionales.
 */
ComposedTable.Thumbail = Thumbail;

/**
 * Componente para previsualizar una imagen o mostrar un ícono de imagen ausente.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.baseUri] - Ruta base para formar el `src` de la imagen.
 * @param {string} [props.path] - Ruta específica de la imagen.
 * @param {string} [props.alt] - Texto alternativo para la imagen.
 * @param {string|number} [props.width] - Ancho del contenedor de la imagen.
 * @param {string|number} [props.height] - Alto del contenedor de la imagen.
 * @param {string|number} [props.aspectRatio] - Aspect ratio del contenedor.
 * @param {Object} [props.style] - Estilos adicionales en línea.
 * @param {string} [props.className] - Clases CSS adicionales para el contenedor.
 * @returns {JSX.Element}
 */
ComposedTable.Preview = Preview;
